import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import laporanService from '../../services/laporan-service';

/* ─── Helpers ─────────────────────────────────────────────── */
const initialFilters = {
  tanggal_mulai: '',
  tanggal_akhir: '',
  jenis_kelamin: '',
  status_pernikahan: '',
  search: '',
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

/* ─── Styles ──────────────────────────────────────────────── */
const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  color: '#475569',
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: '11px',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '12px 16px',
  verticalAlign: 'middle',
};

/* ─── Main Component ──────────────────────────────────────── */
const LaporanPasien = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [laporan, setLaporan] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const fetchLaporan = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = { ...filters };
      Object.keys(params).forEach((key) => {
        if (!params[key]) delete params[key];
      });

      const response = await laporanService.getPatientReport(params);
      setLaporan(response.data || []);
      setSummary(response.summary || null);
      setTotal(response.total || 0);
    } catch (err) {
      console.error(err);
      setError('Gagal mengambil data laporan pasien. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLaporan();
  }, [fetchLaporan]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    setExporting(true);
    setError('');

    try {
      const params = { ...filters };
      Object.keys(params).forEach((key) => {
        if (!params[key]) delete params[key];
      });

      const fileData = await laporanService.exportPatientPdf({ ...params, tipe: 'pasien' });
      downloadBlob(fileData, 'laporan-pasien.pdf');
    } catch (err) {
      console.error(err);
      setError('Gagal mengekspor laporan pasien ke PDF.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout title="Laporan Pasien">
      <div className="dashboard-section">
        
        {/* Header */}
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a' }}>Ringkasan Laporan</h2>
          <Link to="/admin/alur-kerja" className="view-all" style={{ fontSize: '14px', textDecoration: 'none', color: '#2563eb', fontWeight: 500 }}>
            Kembali ke dashboard laporan →
          </Link>
        </div>

        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>
          Gunakan filter di bawah ini untuk melihat laporan pasien berdasarkan periode, jenis kelamin, status pernikahan, dan pencarian.
        </p>

        {/* Filter Form */}
        <div className="form" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
          
          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="tanggal_mulai" style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>Tanggal Mulai</label>
            <input
              id="tanggal_mulai"
              name="tanggal_mulai"
              type="date"
              value={filters.tanggal_mulai}
              onChange={handleChange}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
            />
          </div>

          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="tanggal_akhir" style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>Tanggal Akhir</label>
            <input
              id="tanggal_akhir"
              name="tanggal_akhir"
              type="date"
              value={filters.tanggal_akhir}
              onChange={handleChange}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
            />
          </div>

          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="jenis_kelamin" style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>Jenis Kelamin</label>
            <select
              id="jenis_kelamin"
              name="jenis_kelamin"
              value={filters.jenis_kelamin}
              onChange={handleChange}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff' }}
            >
              <option value="">Semua</option>
              <option value="Laki-laki">Laki-laki</option>
              <option value="Perempuan">Perempuan</option>
            </select>
          </div>

          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="status_pernikahan" style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>Status Pernikahan</label>
            <select
              id="status_pernikahan"
              name="status_pernikahan"
              value={filters.status_pernikahan}
              onChange={handleChange}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff' }}
            >
              <option value="">Semua</option>
              <option value="Belum Menikah">Belum Menikah</option>
              <option value="Menikah">Menikah</option>
              <option value="Duda">Duda</option>
              <option value="Janda">Janda</option>
            </select>
          </div>

          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', gridColumn: 'span 2' }}>
            <label htmlFor="search" style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>Cari</label>
            <input
              id="search"
              name="search"
              type="search"
              placeholder="Nama, NIK, No. pendaftaran, email, telepon"
              value={filters.search}
              onChange={handleChange}
              autoComplete="off"
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
            />
          </div>

          <div className="form-actions" style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px', marginTop: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
            <button type="button" className="btn" onClick={fetchLaporan} style={{ padding: '10px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#2563eb', color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>
              Terapkan Filter
            </button>
            <button type="button" className="btn" onClick={handleExportPdf} disabled={exporting} style={{ padding: '10px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#334155', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>
              {exporting ? 'Mengekspor PDF…' : 'Export PDF'}
            </button>
            <button type="button" className="btn danger" onClick={resetFilters} style={{ padding: '10px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#ef4444', color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer', marginLeft: 'auto' }}>
              Reset Filter
            </button>
          </div>
        </div>

        {error && <p className="form-error" style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px' }}>{error}</p>}

        {/* Stats + Tabel */}
        <div className="dashboard-section" style={{ padding: '0', boxShadow: 'none', background: 'transparent' }}>
          
          {/* Stat Cards */}
          <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #3b82f6' }}>
              <div className="stat-icon" style={{ fontSize: '28px', backgroundColor: '#eff6ff', padding: '12px', borderRadius: '8px' }}>👥</div>
              <div className="stat-info">
                <h3 style={{ fontSize: '13px', color: '#64748b', fontWeight: 500, margin: 0 }}>Total Pasien</h3>
                <p className="stat-value" style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a', margin: '4px 0 0 0' }}>{total.toLocaleString()}</p>
              </div>
            </div>
            
            <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #10b981' }}>
              <div className="stat-icon" style={{ fontSize: '28px', backgroundColor: '#ecfdf5', padding: '12px', borderRadius: '8px' }}>📆</div>
              <div className="stat-info">
                <h3 style={{ fontSize: '13px', color: '#64748b', fontWeight: 500, margin: 0 }}>Pasien Baru Hari Ini</h3>
                <p className="stat-value" style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a', margin: '4px 0 0 0' }}>{summary?.pasien_baru_hari_ini ?? 0}</p>
              </div>
            </div>
            
            <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #8b5cf6' }}>
              <div className="stat-icon" style={{ fontSize: '28px', backgroundColor: '#f5f3ff', padding: '12px', borderRadius: '8px' }}>📅</div>
              <div className="stat-info">
                <h3 style={{ fontSize: '13px', color: '#64748b', fontWeight: 500, margin: 0 }}>Pasien Baru Bulan Ini</h3>
                <p className="stat-value" style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a', margin: '4px 0 0 0' }}>{summary?.pasien_baru_bulan_ini ?? 0}</p>
              </div>
            </div>
          </div>

          {/* Distribusi Jenis Kelamin */}
          {summary?.jenis_kelamin ? (
            <div className="dashboard-section" style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>Distribusi Jenis Kelamin</h3>
              <div className="activity-list" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {summary?.jenis_kelamin?.map((item) => (
                  <div key={item.jenis_kelamin || item.label} className="activity-item" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', flex: '1 1 200px' }}>
                    <span className="activity-icon" style={{ fontSize: '20px' }}>📊</span>
                    <div className="activity-info">
                      <p style={{ margin: 0, fontWeight: 600, color: '#334155' }}>{item.jenis_kelamin || 'Lainnya'}</p>
                      <span className="time" style={{ fontSize: '12px', color: '#64748b' }}>Total: {item.total} pasien</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Tabel Hasil */}
          <div className="dashboard-section" style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#0f172a' }}>Hasil Laporan Pasien</h2>
              <span style={{ fontSize: '13px', color: '#64748b', backgroundColor: '#f1f5f9', padding: '4px 10px', borderRadius: '12px', fontWeight: 500 }}>
                Menampilkan {laporan.length} data
              </span>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                <p>Memuat data laporan...</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <table className="table-report" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', backgroundColor: '#fff' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={thStyle}>Identitas Pasien</th>
                      <th style={thStyle}>Kontak</th>
                      <th style={thStyle}>No. Pendaftaran</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Jenis Kelamin</th>
                      <th style={thStyle}>Terdaftar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {laporan.length > 0 ? (
                      laporan.map((item) => (
                        <tr key={item.id || item.no_identitas || item.no_telepon} style={{ borderBottom: '1px solid #f1f5f9' }} className="table-row-hover">
                          
                          {/* Identitas Pasien */}
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 600, color: '#1e293b' }}>
                              {item.nama || '-'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                              NIK/ID: <span style={{ fontFamily: 'monospace' }}>{item.no_identitas || '-'}</span>
                            </div>
                          </td>

                          {/* Kontak */}
                          <td style={tdStyle}>
                            <div style={{ color: '#334155', fontWeight: 500 }}>{item.no_telepon || '-'}</div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{item.email || '-'}</div>
                          </td>

                          {/* No. Pendaftaran */}
                          <td style={tdStyle}>
                            <span style={{
                              fontFamily: 'monospace',
                              fontWeight: 600,
                              color: '#2563eb',
                              backgroundColor: '#eff6ff',
                              padding: '2px 6px',
                              borderRadius: '4px',
                            }}>
                              {item.no_pendaftaran || '-'}
                            </span>
                          </td>

                          {/* Jenis Kelamin */}
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-flex',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 600,
                              backgroundColor: item.jenis_kelamin === 'Laki-laki' ? '#e0f2fe' : '#fce7f3',
                              color: item.jenis_kelamin === 'Laki-laki' ? '#0369a1' : '#be185d',
                            }}>
                              {item.jenis_kelamin === 'Laki-laki' ? '♂️ L' : item.jenis_kelamin === 'Perempuan' ? '♀️ P' : '-'}
                            </span>
                          </td>

                          {/* Dibuat / Terdaftar */}
                          <td style={{ ...tdStyle, color: '#64748b', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {formatDate(item.created_at)}
                          </td>

                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔍</div>
                          Tidak ada data pasien yang sesuai filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default LaporanPasien;